/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Store from '../../store/store';
import { openModal, closeModal } from '../../actions/modalActions';
import GrayPlus from '../Buttons/grayPlus';
import ResultDetailTask from '../Tasks/resultDetailTask';
import { fetchAllTasks } from '../../actions/taskActions';
import { WorkPackageStatuses } from '../../constants/storyStatusTypes';
import modalTypes from '../../constants/modalTypes';
import { allows } from '../../utils/authorization';
import { scrumSprint } from '../../utils/taskHelpers';
import { isKanbanMode } from '../../selectors/teamSelector';

class ResultTasksTab extends React.Component {
  constructor() {
    super();
    this.openTaskDetail = this.openTaskDetail.bind(this);
    this.closeTaskDetail = this.closeTaskDetail.bind(this);
  }

  componentWillMount() {
    if (this.props.initialTaskLoad) this.props.fetchAllTasks();
  }

  closeTaskDetail(task = {}) {
    this.props.setTaskDetailOpen(false);
    if (task.id) {
      Store.dispatch(closeModal(modalTypes.taskDetail, 'taskDetail'));
    } else {
      Store.dispatch(closeModal(modalTypes.newTask, 'newTask'));
    }
  }

  openTaskDetail(task = {}) {
    this.props.setTaskDetailOpen(true);
    const {
      workPackage,
      createTemporaryTask,
      removeTemporaryTask
    } = this.props;
    const props = {
      workPackage,
      createTemporaryTask,
      removeTemporaryTask,
      task,
      fromResultDetail: true,
      id: task.id,
      onClose: () => this.closeTaskDetail(task),
      workPackageTypeId: workPackage.type_id
    };
    if (task.id) {
      Store.dispatch(openModal(modalTypes.taskDetail, props, 'taskDetail'));
    } else {
      Store.dispatch(openModal(modalTypes.newTask, props, 'newTask'));
    }
  }

  render() {
    const {
      workPackage,
      tasks,
      isKanban,
      initialTaskLoad,
      taskAttachments,
      updateTemporaryTaskOwner,
      updateTemporaryTask
    } = this.props;

    if (initialTaskLoad) return <div id="loader" />;

    const allowCreate = allows('create_tasks', workPackage);
    const completedTasks = tasks
      .map((task, index) => {
        const updatedTask = task;
        if (taskAttachments[index])
          updatedTask.attachments = taskAttachments[index];
        return updatedTask;
      })
      .filter((task) => task.completed);
    const inProgressTasks = tasks
      .map((task, index) => {
        const updatedTask = task;
        if (taskAttachments[index])
          updatedTask.attachments = taskAttachments[index];
        return updatedTask;
      })
      .filter((task) => task.started && !task.completed);
    const readyTasks = tasks
      .map((task, index) => {
        const updatedTask = task;
        if (taskAttachments[index])
          updatedTask.attachments = taskAttachments[index];
        return updatedTask;
      })
      .filter((task) => !task.started && !task.completed);

    const statesSupportNewTask =
      workPackage.state &&
      ![WorkPackageStatuses.NOT_READY, WorkPackageStatuses.LIVE].includes(
        workPackage.state
      );

    const canAddTask = scrumSprint(workPackage, isKanban)
      ? true
      : statesSupportNewTask;

    return (
      <div>
        <div className="right">
          {allowCreate && canAddTask && (
            <GrayPlus handleClick={() => this.openTaskDetail()} />
          )}
        </div>
        <div className="tasks-tab">
          {completedTasks.map((task, index) => (
            <ResultDetailTask
              key={task.id || index}
              task={task}
              status="complete-active"
              setTaskToEdit={this.openTaskDetail}
              closeTaskDetail={this.closeTaskDetail}
              updateTemporaryTaskOwner={updateTemporaryTaskOwner}
              updateTemporaryTask={updateTemporaryTask}
              workPackage={workPackage}
            />
          ))}
          {inProgressTasks.map((task, index) => (
            <ResultDetailTask
              key={task.id || index}
              task={task}
              status="progress-active"
              setTaskToEdit={this.openTaskDetail}
              closeTaskDetail={this.closeTaskDetail}
              updateTemporaryTaskOwner={updateTemporaryTaskOwner}
              updateTemporaryTask={updateTemporaryTask}
              workPackage={workPackage}
            />
          ))}
          {readyTasks.map((task, index) => (
            <ResultDetailTask
              key={task.id || index}
              task={task}
              status="ready-active"
              setTaskToEdit={this.openTaskDetail}
              closeTaskDetail={this.closeTaskDetail}
              updateTemporaryTaskOwner={updateTemporaryTaskOwner}
              updateTemporaryTask={updateTemporaryTask}
              workPackage={workPackage}
            />
          ))}
        </div>
      </div>
    );
  }
}

ResultTasksTab.propTypes = {
  tasks: PropTypes.array,
  workPackage: PropTypes.object
};

const mapStateToProps = (state) => ({
  initialTaskLoad: state.taskReducer.initialDataLoading,
  isKanban: isKanbanMode(state)
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ fetchAllTasks }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ResultTasksTab);
